import { useState } from "react";
import { useTranslation } from "react-i18next";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useLanguageAwareSEO } from "@/hooks/useLanguageAwareSEO";
import { breadcrumbSchema, calculatorToolSchema } from "@/utils/structuredData";

export default function RunningPaceCalculator() {
  const { t } = useTranslation();
  const [distance, setDistance] = useState("");
  const [hours, setHours] = useState("0");
  const [minutes, setMinutes] = useState("30");
  const [seconds, setSeconds] = useState("0");
  const [unit, setUnit] = useState("miles");
  const [results, setResults] = useState<any>(null);

  useLanguageAwareSEO([
    (lang) => breadcrumbSchema([
      { name: t('nav.home'), url: '/' },
      { name: t('categories.lifestyle'), url: '/lifestyle' },
      { name: "Running Pace Calculator", url: '/calculator/running-pace' }
    ], lang),
    (lang) => calculatorToolSchema(
      "Running Pace Calculator",
      "Calculate running pace from distance and time. Enter distance in miles or kilometers and time to find your pace per mile or kilometer and overall speed.",
      "https://calclab.app/calculator/running-pace",
      lang
    )
  ]);

  const calculate = () => {
    const d = parseFloat(distance);
    const h = parseFloat(hours);
    const m = parseFloat(minutes);
    const s = parseFloat(seconds);
    
    if (!d || d <= 0) return;
    
    const totalSeconds = (h * 3600) + (m * 60) + s;
    const totalMinutes = totalSeconds / 60;
    
    const paceMinutes = totalMinutes / d;
    const paceMin = Math.floor(paceMinutes);
    const paceSec = Math.round((paceMinutes - paceMin) * 60);
    
    const speed = (d / totalMinutes) * 60;
    
    setResults({
      pace: `${paceMin}:${String(paceSec).padStart(2, '0')}`,
      speed: speed.toFixed(2),
      unit
    });
  };

  return (
    <PageLayout calculatorName="Running Pace Calculator" calculatorPath="/calculator/running-pace">
      <div className="grid gap-8 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Calculate Pace</CardTitle>
            <CardDescription>From distance and time</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Distance</Label>
              <Input type="number" value={distance} onChange={(e) => setDistance(e.target.value)} placeholder="5" step="0.01" />
            </div>
            <div>
              <Label>Unit</Label>
              <Select value={unit} onValueChange={setUnit}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="miles">Miles</SelectItem>
                  <SelectItem value="km">Kilometers</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label>Hours</Label>
                <Input type="number" value={hours} onChange={(e) => setHours(e.target.value)} placeholder="0" />
              </div>
              <div>
                <Label>Minutes</Label>
                <Input type="number" value={minutes} onChange={(e) => setMinutes(e.target.value)} placeholder="30" />
              </div>
              <div>
                <Label>Seconds</Label>
                <Input type="number" value={seconds} onChange={(e) => setSeconds(e.target.value)} placeholder="0" />
              </div>
            </div>
            <Button onClick={calculate} className="w-full">Calculate</Button>
          </CardContent>
        </Card>

        {results && (
          <>
            <Card>
              <CardHeader>
                <CardTitle>Your Pace</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="text-center mb-4">
                  <div className="text-5xl font-bold text-primary mb-2">{results.pace}</div>
                  <div className="text-lg text-muted-foreground">min/{results.unit === "miles" ? "mile" : "km"}</div>
                </div>
                <div className="border-t pt-4">
                  <div className="flex justify-between text-lg">
                    <span>Speed:</span>
                    <span className="font-semibold">{results.speed} {results.unit}/hour</span>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <CalculatorSuperCalcCTA config={calculatorCTAs["running-pace"]} trigger={results.pace} />
          </>
        )}
      </div>
    </PageLayout>
  );
}
